<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb mb-1">
        <li class="breadcrumb-item"><a routerLink="/logbook/spray-records">Spray Records</a></li>
        <li class="breadcrumb-item active">{{ isEditMode ? 'Edit Record' : 'New Record' }}</li>
      </ol>
    </nav>
    <h1 class="h2 mb-0 fw-bold">{{ isEditMode ? 'Edit Spray Record' : 'New Spray Record' }}</h1>
  </div>
</div>

<form [formGroup]="form" (ngSubmit)="onSubmit()">
  <div class="row g-4"
       [class.dragging]="isDragging"
       (dragover)="onDragOver($event)"
       (dragleave)="onDragLeave($event)"
       (drop)="onDrop($event)">
    <!-- Left Column -->
    <div class="col-lg-8">
      <!-- Flight Path Upload -->
      <div *ngFor="let flight of droneFlights; let i = index; trackBy: trackByFlightId" class="drone-flight-entry mb-4">

        <div class="col-12">
          <!-- Not enforced by onSubmit() — see the drone-flight validation
                block, which deliberately treats the flight path as optional.
                Labelled to match the actual rule rather than showing a `*`
                the form never checks. -->
          <label class="form-label">
            Flight Path <span class="text-muted small fw-normal">(optional)</span>
          </label>
          <div *ngIf="uploadError || isProcessing" class="col-12">
            <div *ngIf="uploadError" class="alert alert-danger mb-0 py-2">
              <i class="bi bi-exclamation-triangle me-2"></i>{{ uploadError }}
            </div>
            <div *ngIf="isProcessing" class="alert alert-info mb-0 py-2">Processing file...</div>
          </div>
          <div *ngIf="!flight.flightPath">
            <button type="button" class="btn btn-outline-primary w-100" (click)="uploadFlightPath(flight)">
              <i class="bi bi-upload me-2"></i>Upload KML/ZIP
            </button>
          </div>
          <div *ngIf="flight.flightPath" style="cursor: pointer" (click)="scrollToSection('flightHeader-' + flight.id, $event)" class="flight-path-display d-flex flex-wrap justify-content-between align-items-center p-2 rounded border text-reset text-decoration-none" id="flightPath-{{ flight.id }}">
            <div class="d-flex align-items-center gap-2">
              <i class="bi bi-bezier2 text-primary"></i>
              <div>
                <div class="small fw-medium">{{ flight.flightPath.name }}</div>
                <div class="small text-muted">
                  {{ flight.flightPath.fileName }}
                  <span *ngIf="flight.flightPath.areaAcres"> &bull; {{ flight.flightPath.areaAcres | number:'1.1-1' }} ac</span>
                </div>
              </div>
            </div>
            <div class="form-switch d-flex align-items-center gap-2 ps-0 mb-0 ms-auto me-5" (click)="$event.stopPropagation()">
              <label class="form-check-label small" [class.fw-bold]="flight.unitSystem === 'metric'" [for]="'unitSystem-' + flight.id">Metric</label>
              <input class="form-check-input m-0" type="checkbox" role="switch"
                     [id]="'unitSystem-' + flight.id"
                     [checked]="flight.unitSystem == 'imperial'"
                     (change)="toggleUnitSystem(flight)">
              <label class="form-check-label small" [class.fw-bold]="flight.unitSystem === 'imperial'" [for]="'unitSystem-' + flight.id">Imperial</label>
            </div>
            <div class="btn-group btn-group-sm">
              <button type="button" class="btn btn-outline-secondary" (click)="viewFlightPath(flight); $event.stopPropagation()" title="View on map">
                <i class="bi bi-eye"></i>
              </button>
              <button type="button" class="btn btn-outline-danger" (click)="removeFlightPath(flight); $event.stopPropagation()" title="Remove">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
        </div>
      </div> <!-- End of drone-flight-entry -->

      <!-- Application Details -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-calendar-event me-2"></i>Application Details</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">Date <span class="text-danger">*</span></label>
              <input type="date" class="form-control" formControlName="date"
                     (change)="onApplicationDateChange()"
                     [class.is-invalid]="isFieldInvalid('date')">
              <div class="invalid-feedback">Date is required</div>
            </div>
            <div class="col-md-6">
              <label class="form-label">Status <span class="text-danger">*</span></label>
              <select class="form-select" formControlName="status">
                <option *ngFor="let status of statuses" [value]="status.value">
                  {{ status.label }}
                </option>
              </select>
            </div>
          </div>
          <div class="form-text mt-2">
            <i class="bi bi-info-circle me-1"></i>
            Start and end times are tracked per drone flight below.
          </div>
          <div *ngIf="form.get('status')?.value === 'draft'" class="alert alert-warning border mt-3 mb-0 py-2 small">
            <i class="bi bi-pencil me-1"></i>
            <strong>Draft:</strong> only the date is required &mdash; fields marked
            <span class="text-danger">*</span> are <em>not</em> enforced and can be saved blank.
            Change status to Pending or Completed to have them checked.
          </div>
        </div>
      </div>

      <!-- Field Information -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-geo-alt me-2"></i>Field Information</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">Field <span class="text-danger">*</span></label>
              <div class="input-group">
                <select class="form-select" formControlName="fieldId"
                        [class.is-invalid]="isFieldInvalid('fieldId')">
                  <option value="">Select a field...</option>
                  <option *ngFor="let field of fields" [value]="field.id">
                    {{ getFieldDisplayName(field) }}
                  </option>
                </select>
                <button type="button" class="btn btn-outline-primary" (click)="openFieldQuickAdd()" title="Add new field">
                  <i class="bi bi-plus-lg"></i>
                </button>
              </div>
              <div class="invalid-feedback">Please select a field</div>
            </div>
            <div class="col-md-3">
              <label class="form-label">Acreage</label>
              <div class="input-group">
                <input type="text" class="form-control" formControlName="acreage" readonly>
                <span class="input-group-text">ac</span>
              </div>
            </div>
            <div class="col-md-3">
              <label class="form-label">Crop Type</label>
              <input type="text" class="form-control" formControlName="cropType" readonly>
            </div>

            <!-- Customer Display (shown when field has a customer) -->
            <div class="col-12" *ngIf="selectedCustomer">
              <div class="d-flex align-items-center gap-2 p-2 border rounded">
                <i class="bi bi-person-fill text-primary"></i>
                <span class="text-body-secondary">Customer:</span>
                <a [routerLink]="['/customers', selectedCustomer.id, 'view']" class="text-decoration-none fw-medium text-primary">
                  {{ selectedCustomer.name }}
                </a>
              </div>
            </div>

            <!-- Coordinates Display (shown when field is selected) -->
            <div class="col-12" *ngIf="selectedField">
              <hr class="my-2">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label d-flex align-items-center gap-2">
                    <i class="bi bi-pin-map text-muted"></i>
                    Coordinates
                    <button type="button" class="btn btn-link btn-sm p-0 ms-1" (click)="toggleCoordinateDisplay()" title="Toggle format">
                      <i class="bi bi-arrow-repeat small"></i>
                    </button>
                  </label>
                  <div class="input-group">
                    <input type="text" class="form-control" [value]="formatCoordinates(selectedField)" readonly>
                    <button type="button" class="btn btn-outline-secondary" (click)="copyCoordinates()" title="Copy coordinates">
                      <i class="bi bi-clipboard"></i>
                    </button>
                    <button type="button" class="btn btn-outline-secondary" (click)="openInMaps()" title="View on map">
                      <i class="bi bi-box-arrow-up-right"></i>
                    </button>
                  </div>
                  <div class="form-text">
                    {{ coordinateDisplayMode === 'decimal' ? 'Decimal degrees' : 'Degrees, minutes, seconds' }}
                  </div>
                </div>
                <div class="col-md-6" *ngIf="selectedField?.address">
                  <label class="form-label d-flex align-items-center gap-2">
                    <i class="bi bi-building text-muted"></i>
                    Address
                  </label>
                  <input type="text" class="form-control" [value]="selectedField.address" readonly>
                  <div class="form-text">&nbsp;</div>
                </div>
              </div>

              <!-- Load/Mix Location -->
              <hr class="my-2">
              <div class="form-check mb-2">
                <input class="form-check-input" type="checkbox" id="useLocationForLoadMixLocation"
                       formControlName="useLocationForLoadMixLocation">
                <label class="form-check-label" for="useLocationForLoadMixLocation">
                  Same location for chemical loading/mixing
                </label>
              </div>
              <div *ngIf="showLoadMixLocationFields" class="row g-3">
                <div class="col-md-6">
                  <label class="form-label">Load/Mix Location Latitude</label>
                  <input type="number" class="form-control" formControlName="loadMixLocationLatitude"
                         placeholder="e.g., 39.82" step="any">
                </div>
                <div class="col-md-6">
                  <label class="form-label">Load/Mix Location Longitude</label>
                  <input type="number" class="form-control" formControlName="loadMixLocationLongitude"
                         placeholder="e.g., -98.58" step="any">
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Spray Details -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-droplet me-2"></i>Spray Details</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">What Was Treated</label>
              <input type="text" class="form-control" formControlName="whatWasTreated"
                     placeholder="e.g., Corn, Soybeans, Cover crop">
            </div>
            <div class="col-md-6">
              <label class="form-label">Target Pest</label>
              <input type="text" class="form-control" formControlName="targetPest"
                     placeholder="e.g., Broadleaf weeds, Japanese beetles">
            </div>
            <div class="col-md-4">
              <label class="form-label">Nozzle Description</label>
              <input type="text" class="form-control" formControlName="nozzleDescription"
                     placeholder="e.g., TeeJet XR11004">
            </div>
            <div class="col-md-4">
              <label class="form-label">Nozzle to Target Distance</label>
              <app-unit-input
                formControlName="nozzleToTargetDistance"
                [units]="distanceUnits"
                [defaultUnit]="defaultUnits.distance"
                [placeholder]="'10'">
              </app-unit-input>
            </div>
            <div class="col-md-4">
              <label class="form-label">Pump Pressure</label>
              <app-unit-input
                formControlName="pumpPressure"
                [units]="pressureUnits"
                [defaultUnit]="defaultUnits.pressure"
                [placeholder]="'40'">
              </app-unit-input>
            </div>
            <div class="col-md-6">
              <label class="form-label">Travel Speed</label>
              <app-unit-input
                formControlName="travelSpeed"
                [units]="speedUnits"
                [defaultUnit]="defaultUnits.speed"
                [placeholder]="'12'">
              </app-unit-input>
            </div>
            <div class="col-md-6">
              <label class="form-label">Drift Management Practices</label>
              <textarea appCharCounter class="form-control" formControlName="descriptionOfDriftManagementPractices"
                        rows="2" placeholder="Describe drift mitigation measures..."></textarea>
            </div>

            <!-- Application Types -->
            <div class="col-12">
              <hr class="my-2">
              <label class="form-label">Application Types</label>
              <div class="d-flex flex-wrap gap-3">
                <div *ngFor="let option of applicationTypeOptions" class="form-check">
                  <input class="form-check-input" type="checkbox"
                         [id]="'appType_' + option.value"
                         [formControlName]="'appType_' + option.value">
                  <label class="form-check-label" [for]="'appType_' + option.value">
                    {{ option.label }}
                  </label>
                </div>
              </div>
            </div>
            <div class="col-md-6" *ngIf="showApplicationTypeOther">
              <label class="form-label">Other Application Type</label>
              <input type="text" class="form-control" formControlName="applicationTypeOther"
                     placeholder="Describe other application type...">
            </div>
          </div>
        </div>
      </div>

      <!-- Chemical Information -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0">
            <i class="bi bi-flask me-2"></i>Chemical Information
            <span class="text-danger" title="At least one chemical is required">*</span>
          </h5>
        </div>
        <div class="card-body">
          <!-- Applied Chemicals List -->
          <div class="mb-4" *ngIf="appliedChemicals.length > 0">
            <label class="form-label">Applied Chemicals <span class="text-danger">*</span></label>
            <div class="applied-chemicals-list">
              <div *ngFor="let chem of appliedChemicals; trackBy: trackByChemicalId" class="applied-chemical-item d-flex justify-content-between align-items-center p-2 rounded mb-2">
                <div class="d-flex align-items-center gap-3 flex-grow-1">
                  <div class="chemical-info">
                    <div class="d-flex align-items-center gap-2">
                      <span class="fw-medium">{{ chem.name }}</span>
                      <span class="badge" [ngClass]="getSourceBadgeClass(chem.source)">{{ getSourceLabel(chem.source) }}</span>
                    </div>
                    <div class="small text-muted">
                      EPA #{{ chem.epaNumber }}
                      <span *ngIf="chem.templateName"> &bull; From: {{ chem.templateName }}</span>
                    </div>
                  </div>
                  <div class="rate-input">
                    <div class="input-group input-group-sm w-200px">
                      <input type="number" class="form-control form-control-sm"
                             [value]="chem.rateValue"
                             (change)="updateChemicalRateValue(chem, $any($event.target).valueAsNumber || null)"
                             min="0" step="0.01"
                             placeholder="Rate">
                      <select class="form-select form-select-sm mw-90px"
                              (change)="updateChemicalRateUnit(chem, $any($event.target).value)">
                        <option *ngFor="let unit of rateUnitOptions"
                              [value]="unit.value"
                              [selected]="unit.value === chem.rateUnit">{{ unit.label }}</option>
                      </select>
                    </div>
                  </div>
                </div>
                <button type="button" class="btn btn-sm btn-outline-danger ms-2" (click)="removeChemical(chem)" title="Remove">
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Empty state for no chemicals -->
          <div *ngIf="appliedChemicals.length === 0 && validationErrors.length === 0" class="alert alert-light border mb-4">
            <div class="d-flex align-items-center gap-2">
              <i class="bi bi-info-circle text-muted"></i>
              <span class="text-muted">
                At least one chemical is required. Use the options below to add chemicals.
              </span>
            </div>
          </div>
          <div *ngIf="appliedChemicals.length === 0 && validationErrors.length > 0" class="alert alert-danger mb-4 py-2">
            <div class="d-flex align-items-center gap-2">
              <i class="bi bi-exclamation-circle"></i>
              <span>At least one chemical is required.</span>
            </div>
          </div>

          <hr class="my-3">

          <!-- Add Chemicals Section -->
          <div class="add-chemicals-section">
            <label class="form-label fw-medium">Add Chemicals</label>

            <!-- Source Toggle -->
            <div class="btn-group w-100 mb-3" role="group">
              <input type="radio" class="btn-check" id="sourceLookup" formControlName="chemicalSource" value="lookup">
              <label class="btn btn-outline-primary" for="sourceLookup">
                <i class="bi bi-book me-2"></i>Chemical Book / Custom
              </label>
              <input type="radio" class="btn-check" id="sourceTemplate" formControlName="chemicalSource" value="template">
              <label class="btn btn-outline-primary" for="sourceTemplate">
                <i class="bi bi-layers me-2"></i>From Template
              </label>
            </div>

            <!-- Chemical Lookup (Book / Custom) -->
            <div class="row g-3" *ngIf="isChemicalLookup">
              <div class="col-md-5">
                <app-chemical-typeahead
                  #chemicalTypeahead
                  placeholder="Search chemicals..."
                  [showCategory]="true"
                  [showEpaNumber]="true"
                  (chemicalSelected)="onChemicalSelected($event)"
                  (cleared)="onChemicalCleared()">
                </app-chemical-typeahead>
              </div>
              <div class="col-md-4">
                <div class="input-group">
                  <input type="number" class="form-control" formControlName="applicationRateValue"
                         placeholder="Rate" min="0" step="0.01">
                  <select class="form-select mw-110px" formControlName="applicationRateUnit">
                    <option *ngFor="let unit of rateUnitOptions" [value]="unit.value">{{ unit.label }}</option>
                  </select>
                </div>
              </div>
              <div class="col-md-3">
                <button type="button" class="btn w-100"
                        [class.btn-primary]="selectedChemical && form.get('applicationRateValue')?.value"
                        [class.btn-outline-primary]="!selectedChemical || !form.get('applicationRateValue')?.value"
                        (click)="addChemical()"
                        [disabled]="!selectedChemical || !form.get('applicationRateValue')?.value">
                  <i class="bi bi-plus-lg me-1"></i>Add
                </button>
              </div>
            </div>

            <!-- Chemical Template Selection -->
            <div class="row g-3" *ngIf="isChemicalTemplate">
              <div class="col-md-9">
                <select class="form-select" formControlName="chemicalTemplateId">
                  <option value="">Select a template...</option>
                  <option *ngFor="let template of chemicalTemplates" [value]="template.id">
                    {{ template.name }}
                  </option>
                </select>
              </div>
              <div class="col-md-3">
                <button type="button" class="btn btn-outline-primary w-100"
                        (click)="applyTemplate()"
                        [disabled]="!form.get('chemicalTemplateId')?.value">
                  <i class="bi bi-plus-lg me-1"></i>Apply
                </button>
              </div>

              <!-- Template Preview -->
              <div class="col-12" *ngIf="selectedTemplate">
                <div class="template-details p-3 rounded">
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <h6 class="mb-0 fw-medium">{{ selectedTemplate.name }}</h6>
                    <div class="d-flex gap-1">
                      <span *ngFor="let crop of selectedTemplate.targetCrops" class="badge bg-success-subtle text-success">
                        {{ crop }}
                      </span>
                    </div>
                  </div>
                  <p class="small text-muted mb-3">{{ selectedTemplate.description }}</p>

                  <!-- Chemicals in Template -->
                  <div class="mb-3">
                    <label class="form-label small text-muted mb-1">Chemicals to be added:</label>
                    <div class="template-chemicals">
                      <div *ngFor="let item of selectedTemplate.items" class="template-chemical-item d-flex justify-content-between py-1 border-bottom">
                        <span class="small">{{ item.name }}</span>
                        <span class="small fw-medium">{{ item.rate }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Target Pests -->
                  <div>
                    <label class="form-label small text-muted mb-1">Target pests:</label>
                    <div class="d-flex flex-wrap gap-1">
                      <span *ngFor="let pest of selectedTemplate.targetPests" class="badge bg-warning-subtle text-warning-emphasis">
                        {{ pest }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <hr class="my-3">

          <!-- Total Volume & Tank Mix Notes -->
          <div class="row g-3">
            <div class="col-md-4">
              <label class="form-label">Total Volume</label>
              <app-unit-input
                formControlName="totalVolume"
                [units]="volumeUnits"
                [defaultUnit]="defaultUnits.volume"
                [placeholder]="'50'">
              </app-unit-input>
            </div>
            <div class="col-md-8">
              <label class="form-label">Tank Mix Notes</label>
              <textarea appCharCounter class="form-control" formControlName="tankMixNotes" rows="1"
                        placeholder="Additional tank mix components, adjuvants, etc."></textarea>
            </div>
          </div>
        </div>
      </div>

      <!-- Operating Certification (FAA 44807) -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-award me-2"></i>Operating Certification</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-12">
              <label class="form-label">FAA 44807 Exemption <span class="text-danger">*</span></label>
              <select class="form-select" formControlName="certificationId"
                      [class.is-invalid]="isFieldInvalid('certificationId')">
                <option value="">Select a certification...</option>
                <option *ngFor="let cert of certifications" [value]="cert.id">
                  {{ cert.name }} ({{ cert.certNumber }})
                </option>
              </select>
              <div class="invalid-feedback">Your organization needs an active FAA 44807 exemption</div>
              <div class="form-text" *ngIf="selectedCertification">
                Expires: {{ selectedCertification.expirationDate }}
              </div>
              <div class="alert alert-warning mt-2 py-2" *ngIf="certifications.length === 0 && !isLoadingCertifications">
                <i class="bi bi-exclamation-triangle me-2"></i>
                No active FAA 44807 exemption found. Please add one in your organization settings.
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Visual Observer & NOTAM -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-eye me-2"></i>Visual Observer & NOTAM</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-5">
              <label class="form-label">Visual Observer</label>
              <input type="text" class="form-control" formControlName="visualObserver"
                     placeholder="VO name">
            </div>
            <div class="col-md-3">
              <label class="form-label">VO Tested</label>
              <div class="form-check pt-2">
                <input class="form-check-input" type="checkbox" id="voTested"
                       formControlName="voTested">
                <label class="form-check-label" for="voTested">Yes</label>
              </div>
            </div>
            <div class="col-md-4">
              <label class="form-label">NOTAM Number</label>
              <input type="text" class="form-control" formControlName="notam"
                     placeholder="e.g., MM/NNN">
              <div class="form-text">
                Flight Services: <a href="tel:+18774876867">1-877-487-6867</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Drone Flights -->
      <div class="card mb-4">
        <div class="card-header p-3 d-flex flex-wrap justify-content-between align-items-center">
          <h5 class="mb-0"><i class="icon-drone me-2"></i>Drone Flights</h5>
          <div class="d-flex gap-2">
            <button *ngIf="getAllFlightPaths().length > 0" type="button" class="btn btn-sm btn-outline-secondary" (click)="viewAllFlightPaths()" title="View all flight paths on map">
              <i class="bi bi-map me-1"></i>View Map
            </button>
            <button *ngIf="showAddDroneButton" type="button" class="btn btn-sm btn-outline-primary" (click)="addDroneFlight()">
              <i class="bi bi-plus-lg me-1"></i>Add Drone
            </button>
          </div>
        </div>
        <div class="card-body">
          <!-- Acreage Progress (shown when field is selected) -->
          <div *ngIf="selectedField" class="acreage-progress mb-4">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="text-muted small">Field Coverage</span>
              <span class="small">
                <span class="fw-medium">{{ totalAcreageSprayed | number:'1.1-1' }}</span>
                <span class="text-muted"> / {{ fieldAcreage }} acres</span>
                <span *ngIf="totalAcreageSprayed > fieldAcreage" class="text-muted">({{ (totalAcreageSprayed / fieldAcreage * 100) | number:'1.0-0' }}%)</span>
              </span>
            </div>
            <div class="progress h-8px">
              <div class="progress-bar"
                   [class.bg-success]="totalAcreageSprayed > 0 && totalAcreageSprayed <= fieldAcreage"
                   [class.bg-info]="totalAcreageSprayed > fieldAcreage"
                   [style.width.%]="fieldAcreage > 0 ? Math.min(totalAcreageSprayed / fieldAcreage * 100, 100) : 0">
              </div>
            </div>
            <div *ngIf="totalAcreageSprayed > fieldAcreage" class="form-text text-info">
              <i class="bi bi-info-circle me-1"></i>Coverage exceeds field size (overlap or multiple passes)
            </div>
          </div>

          <!-- Drone Flight Entries -->
          <div *ngFor="let flight of droneFlights; let i = index; trackBy: trackByFlightId" class="drone-flight-entry mb-4" id="flightHeader-{{ flight.id }}">
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
              <h6 class="mb-0 d-flex align-items-center gap-2">
                <span class="badge bg-primary rounded-pill">{{ i + 1 }}</span>
                <span *ngIf="getDroneName(flight.droneId)">{{ getDroneName(flight.droneId) }}</span>
                <span *ngIf="!getDroneName(flight.droneId)" class="text-muted">Drone Flight {{ i + 1 }}</span>
                <span *ngIf="flight.flightPath?.fileName">•</span>
                <a *ngIf="flight.flightPath?.fileName" class="text-muted text-decoration-none" href="#" (click)="scrollToSection('flightPath-' + flight.id, $event)">{{ flight.flightPath?.fileName }} </a>
              </h6>
              <div class="d-flex align-items-center gap-3">
                <div class="form-switch d-flex align-items-center gap-2 ps-0 mb-0">
                  <label class="form-check-label small" [class.fw-bold]="flight.unitSystem === 'metric'" [for]="'unitSystem-' + flight.id">Metric</label>
                  <input class="form-check-input m-0" type="checkbox" role="switch"
                        [id]="'unitSystem-' + flight.id"
                        [checked]="flight.unitSystem === 'imperial'"
                        (change)="toggleUnitSystem(flight)">
                  <label class="form-check-label small" [class.fw-bold]="flight.unitSystem === 'imperial'" [for]="'unitSystem-' + flight.id">Imperial</label>
                </div>
                <button *ngIf="droneFlights.length > 1" type="button" class="btn btn-sm btn-outline-danger" (click)="removeDroneFlight(i)" title="Remove this drone flight">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>

            <div class="row g-3 mx-0">
              <!-- Drone & Pilot Selection -->
              <div class="col-md-6">
                <label class="form-label">Drone <span class="text-danger">*</span></label>
                <select class="form-select" [(ngModel)]="flight.droneId" [ngModelOptions]="{standalone: true}"
                        (ngModelChange)="onDroneSelectionChange(i)"
                        [class.is-invalid]="showDroneFlightErrors && !flight.droneId">
                  <option value="">Select a drone...</option>
                  <option *ngFor="let drone of getAvailableDrones(flight.id); trackBy: trackByDroneId" [value]="drone.id">
                    {{ drone.name }}
                  </option>
                </select>
                <div class="invalid-feedback">Drone is required</div>
              </div>
              <div class="col-md-6">
                <label class="form-label">Pilot <span class="text-danger">*</span></label>
                <select class="form-select" [(ngModel)]="flight.pilotId" [ngModelOptions]="{standalone: true}"
                        [class.is-invalid]="showDroneFlightErrors && !flight.pilotId">
                  <option value="">Select a pilot...</option>
                  <option *ngFor="let pilot of pilots; trackBy: trackByPilotId" [value]="pilot.id">
                    {{ pilot.name }}
                  </option>
                </select>
                <div class="invalid-feedback">Pilot is required</div>
              </div>

              <!-- Flight Times -->
              <div class="col-md-3">
                <label class="form-label">Start Time <span class="text-danger">*</span></label>
                <input type="time" class="form-control" [(ngModel)]="flight.startTime" [ngModelOptions]="{standalone: true}"
                       (ngModelChange)="onFlightScheduleChange(i)"
                       [class.is-invalid]="showDroneFlightErrors && !flight.startTime">
                <div class="invalid-feedback">Start time is required</div>
              </div>
              <div class="col-md-3">
                <label class="form-label">End Time <span class="text-danger">*</span></label>
                <input type="time" class="form-control" [(ngModel)]="flight.endTime" [ngModelOptions]="{standalone: true}"
                       (ngModelChange)="onFlightScheduleChange(i)"
                       [class.is-invalid]="showDroneFlightErrors && !flight.endTime">
                <div class="invalid-feedback">End time is required</div>
              </div>

              <!-- Already flying for someone else at this time -->
              <div class="col-12" *ngIf="flightConflicts.has(flight.id)">
                <div class="alert alert-danger py-2 px-3 mb-0 small" role="alert">
                  <i class="bi bi-exclamation-triangle-fill me-1"></i>
                  {{ flightConflicts.get(flight.id) }}
                </div>
              </div>

              <!-- Flight Parameters -->
              <div class="col-md-3">
                <label class="form-label">Altitude</label>
                <div class="input-group">
                  <input type="number" class="form-control" [(ngModel)]="flight.flightAltitude" [ngModelOptions]="{standalone: true}"
                         placeholder="15" min="1" max="400">
                  <span class="input-group-text">{{ flight.unitSystem === 'imperial' ? 'ft' : 'm' }}</span>
                </div>
              </div>
              <div class="col-md-3">
                <label class="form-label">Speed</label>
                <div class="input-group">
                  <input type="number" class="form-control" [(ngModel)]="flight.flightSpeed" [ngModelOptions]="{standalone: true}"
                         placeholder="12" min="1" max="30">
                  <span class="input-group-text">{{ flight.unitSystem === 'imperial' ? 'mph' : 'm/s' }}</span>
                </div>
              </div>

              <!-- Number of Flights (FAA §F.2.d)
                   Belongs beside the aircraft it describes, never as a
                   record-level field: a record-level control reads as "flights
                   on this job" and the per-aircraft grain dies in the UI even
                   though it survives in the schema. -->
              <div class="col-md-4">
                <label class="form-label">Number of Flights</label>
                <input type="number" class="form-control"
                       [(ngModel)]="flight.numFlights" [ngModelOptions]="{standalone: true}"
                       placeholder="e.g. 3" min="1" step="1">
                <div class="form-text">
                  Takeoffs and landings by this aircraft at this location. Leave blank if unknown.
                </div>
              </div>

              <!-- Acreage Sprayed -->
              <div class="col-md-4">
                <label class="form-label">Acreage Sprayed <span class="text-danger">*</span></label>
                <div class="input-group" [class.has-validation]="showDroneFlightErrors && !flight.acreageSprayed">
                  <input type="number" class="form-control"
                         [value]="flight.acreageSprayed"
                         (input)="updateDroneAcreage(flight, $any($event.target).value)"
                         placeholder="0" min="0" step="0.1"
                         [class.is-invalid]="showDroneFlightErrors && !flight.acreageSprayed">
                  <span class="input-group-text">ac</span>
                  <div class="invalid-feedback">Acreage is required</div>
                </div>
              </div>

              <!-- Chemical Usage for this Drone (auto-calculated) -->
              <div class="col-12" *ngIf="flight.chemicalUsage.length > 0">
                <label class="form-label d-flex align-items-center gap-2">
                  <i class="bi bi-calculator text-muted"></i>
                  Chemical Usage <span class="badge bg-secondary-subtle text-secondary">Auto-calculated</span>
                </label>
                <div class="chemical-usage-list p-2 rounded border">
                  <div *ngFor="let cu of flight.chemicalUsage; trackBy: trackByChemicalUsageId" class="d-flex justify-content-between py-1 border-bottom">
                    <span class="small">{{ cu.chemicalName }}</span>
                    <span class="small fw-medium">{{ cu.amountUsed }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Separator between drone entries -->
            <hr *ngIf="i < droneFlights.length - 1" class="mt-4">
          </div>

          <!-- Add Drone Button (alternative location) -->
          <div *ngIf="showAddDroneButton && droneFlights.length > 0" class="text-center">
            <button type="button" class="btn btn-outline-secondary btn-sm" (click)="addDroneFlight()">
              <i class="bi bi-plus-lg me-1"></i>Add Another Drone
            </button>
          </div>

          <!-- No Drones Warning -->
          <div *ngIf="drones.length === 0" class="alert alert-warning">
            <i class="bi bi-exclamation-triangle me-2"></i>No drones available. Please add drones to your organization first.
          </div>
        </div>
      </div>

      <!-- Weather Conditions -->
      <div class="card mb-4">
        <div class="card-header p-3 d-flex justify-content-between align-items-center">
          <h5 class="mb-0"><i class="bi bi-cloud-sun me-2"></i>Weather Conditions</h5>
          <button type="button" class="btn btn-sm btn-outline-primary"
                  (click)="lookupWeather()"
                  [disabled]="!canLookupWeather || isLoadingWeather"
                  [title]="canLookupWeather ? 'Lookup weather for selected field, date, and time' : 'Select field, date, and start time to enable lookup'">
            <ng-container *ngIf="!isLoadingWeather">
              <i class="bi bi-cloud-download me-1"></i>Lookup Weather
            </ng-container>
            <ng-container *ngIf="isLoadingWeather">
              <span class="spinner-border spinner-border-sm me-1" role="status"></span>Looking up...
            </ng-container>
          </button>
        </div>
        <div class="card-body">
          <!-- Weather lookup feedback -->
          <div *ngIf="weatherError" class="alert alert-danger alert-dismissible py-2 mb-3">
            <i class="bi bi-exclamation-triangle me-2"></i>{{ weatherError }}
            <button type="button" class="btn-close btn-sm" (click)="weatherError = null"></button>
          </div>
          <div *ngIf="weatherLookupSuccess" class="alert alert-success py-2 mb-3">
            <i class="bi bi-check-circle me-2"></i>Weather data populated
          </div>

          <div class="row g-3">
            <div class="col-md-3">
              <label class="form-label">Temperature</label>
              <div class="input-group">
                <input type="number" class="form-control" formControlName="temperature"
                       placeholder="72">
                <span class="input-group-text">°F</span>
              </div>
            </div>
            <div class="col-md-3">
              <label class="form-label">Humidity</label>
              <div class="input-group">
                <input type="number" class="form-control" formControlName="humidity"
                       placeholder="45" min="0" max="100">
                <span class="input-group-text">%</span>
              </div>
            </div>
            <div class="col-md-3">
              <label class="form-label">Wind Speed</label>
              <div class="input-group">
                <input type="number" class="form-control" formControlName="windSpeed"
                       placeholder="5" min="0" max="50">
                <span class="input-group-text">mph</span>
              </div>
            </div>
            <div class="col-md-3">
              <label class="form-label">Wind Direction</label>
              <select class="form-select" formControlName="windDirection">
                <option value="">Select...</option>
                <option *ngFor="let dir of windDirections" [value]="dir">{{ dir }}</option>
              </select>
            </div>
            <div class="col-12">
              <label class="form-label">Conditions</label>
              <input type="text" class="form-control" formControlName="weatherConditions"
                     placeholder="e.g., Clear, Partly cloudy, Overcast">
            </div>
          </div>

          <!-- Helper text for weather lookup -->
          <div class="form-text mt-3" *ngIf="!canLookupWeather">
            <i class="bi bi-info-circle me-1"></i>
            Select a field with coordinates, date, and start time to enable weather lookup.
          </div>
        </div>
      </div>

      <!-- Incident Information -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-exclamation-triangle me-2"></i>Incident Information</h5>
        </div>
        <div class="card-body">
          <!-- Takeoff/Landing Damage -->
          <div class="mb-3">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="takeOffOrLandingDamage"
                     formControlName="takeOffOrLandingDamage">
              <label class="form-check-label" for="takeOffOrLandingDamage">
                Take Off or Landing Damage
              </label>
            </div>
            <div *ngIf="showTakeOffDamageDescription" class="mt-2 ps-4">
              <label class="form-label small">Description</label>
              <textarea appCharCounter class="form-control" formControlName="descriptionOfTakeOffOrLandingDamage"
                        rows="2" placeholder="Describe the damage..."></textarea>
            </div>
          </div>

          <hr class="my-3">

          <!-- Equipment Malfunction -->
          <div class="mb-3">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="equipmentMalfunction"
                     formControlName="equipmentMalfunction">
              <label class="form-check-label" for="equipmentMalfunction">
                Equipment Malfunction
              </label>
            </div>
            <div *ngIf="showEquipmentMalfunctionDetails" class="mt-2 ps-4">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label small">Malfunction Type</label>
                  <select class="form-select" formControlName="equipmentMalfunctionType">
                    <option value="">Select type...</option>
                    <option *ngFor="let type of equipmentMalfunctionTypes" [value]="type">
                      {{ type }}
                    </option>
                  </select>
                </div>
                <div class="col-12">
                  <label class="form-label small">Description</label>
                  <textarea appCharCounter class="form-control" formControlName="descriptionOfEquipmentMalfunction"
                            rows="2" placeholder="Describe the malfunction..."></textarea>
                </div>
              </div>
            </div>
          </div>

          <hr class="my-3">

          <!-- Accident -->
          <div class="mb-0">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="hasAccident"
                     formControlName="hasAccident">
              <label class="form-check-label" for="hasAccident">
                Accident
              </label>
            </div>
            <div *ngIf="showAccidentDetails" class="mt-2 ps-4">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label small">Accident Type</label>
                  <select class="form-select" formControlName="accidentType">
                    <option value="">Select type...</option>
                    <option *ngFor="let type of accidentTypes" [value]="type">
                      {{ type }}
                    </option>
                  </select>
                </div>
                <div class="col-md-6">
                  <label class="form-label small">UA Loss Type</label>
                  <select class="form-select" formControlName="uaLossType">
                    <option value="">Select type...</option>
                    <option *ngFor="let type of uaLossTypes" [value]="type">
                      {{ type }}
                    </option>
                  </select>
                </div>
                <div class="col-12">
                  <label class="form-label small">Description</label>
                  <textarea appCharCounter class="form-control" formControlName="accidentDescription"
                            rows="2" placeholder="Describe the accident..."></textarea>
                </div>
              </div>
              <div class="alert alert-warning mt-3 mb-0 py-2">
                <i class="bi bi-info-circle me-2"></i>
                <strong>Note:</strong> Fatal injuries must be reported within 30 days.
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Safety & Compliance -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-shield-check me-2"></i>Safety & Compliance</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <!-- Re-entry Interval -->
            <div class="col-md-4">
              <label class="form-label">Re-Entry Interval</label>
              <app-unit-input
                formControlName="reentryInterval"
                [units]="timeIntervalUnits"
                [defaultUnit]="defaultUnits.timeInterval"
                [placeholder]="'24'">
              </app-unit-input>
              <div class="form-text">Duration before field re-entry is safe</div>
            </div>

            <!-- PPE Required -->
            <div class="col-md-8">
              <label class="form-label">PPE Required</label>
              <div class="d-flex flex-wrap gap-3">
                <div *ngFor="let ppe of ppeOptions" class="form-check">
                  <input class="form-check-input" type="checkbox"
                         [id]="'ppe_' + ppe.value"
                         [formControlName]="'ppe_' + ppe.value">
                  <label class="form-check-label" [for]="'ppe_' + ppe.value">
                    {{ ppe.label }}
                  </label>
                </div>
              </div>
            </div>

            <!-- Equipment Rinsing -->
            <div class="col-12">
              <label class="form-label">Equipment Rinsing Procedures</label>
              <textarea appCharCounter class="form-control" formControlName="rinseEquipmentPostApplication"
                        rows="2" placeholder="Describe post-application equipment rinsing..."></textarea>
            </div>
          </div>
        </div>
      </div>

      <!-- Emergency Information -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-telephone me-2"></i>Emergency Information</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">Emergency Contact Number</label>
              <input type="tel" class="form-control" formControlName="emergencyContactNumber"
                     placeholder="(555) 123-4567" mask="(000) 000-0000">
            </div>
            <div class="col-md-6">
              <label class="form-label">State Emergency Contact Number</label>
              <input type="tel" class="form-control" formControlName="stateEmergencyContactNumber"
                     placeholder="(555) 123-4567" mask="(000) 000-0000">
            </div>
          </div>
        </div>
      </div>

      <!-- Notes -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-chat-left-text me-2"></i>Additional Notes</h5>
        </div>
        <div class="card-body">
          <textarea appCharCounter class="form-control" formControlName="notes" rows="4"
                    placeholder="Any additional notes about this spray application..."></textarea>
        </div>
      </div>

    </div>

    <!-- Right Column - Sidebar -->
    <div class="col-lg-4">
     <div class="sticky-top sticky-sidebar">
      <!-- Tips Card -->
      <div class="card mb-4">
        <div class="card-body">
          <h6 class="d-flex align-items-center gap-2 mb-3">
            <i class="bi bi-lightbulb text-warning"></i>
            Tips
          </h6>
          <ul class="small text-muted mb-0 ps-3">
            <li class="mb-2">Record weather conditions at the start of application</li>
            <li class="mb-2">Wind speeds above 10 mph may require spray adjustments</li>
            <li class="mb-2">Verify buffer zones before each application</li>
            <li>Complete pre-flight checklist for regulatory compliance</li>
          </ul>
        </div>
      </div>

      <!-- Summary Card -->
      <div class="card summary-card">
        <div class="card-header p-3">
          <h5 class="mb-0">Record Summary</h5>
        </div>
        <div class="card-body">
          <div class="summary-item" *ngIf="selectedField">
            <div class="summary-label text-muted">Field</div>
            <div class="summary-value">{{ selectedField.name }}</div>
          </div>
          <div class="summary-item" *ngIf="selectedField">
            <div class="summary-label text-muted">Field Acreage</div>
            <div class="summary-value">{{ selectedField.acreage }} acres</div>
          </div>

          <!-- Drone Flights Summary -->
          <div class="summary-item" *ngIf="droneFlights.length > 0">
            <div class="summary-label text-muted">Drones ({{ droneFlights.length }})</div>
            <div class="summary-drones mt-1">
              <div *ngFor="let flight of droneFlights; trackBy: trackByFlightId" class="d-flex justify-content-between small py-1 border-bottom border-light">
                <span class="text-truncate mw-140px" [title]="getDroneName(flight.droneId) || 'Not selected'">
                  {{ getDroneName(flight.droneId) || 'Not selected' }}
                </span>
                <span class="text-muted">{{ flight.acreageSprayed || 0 }} ac</span>
              </div>
            </div>
          </div>

          <!-- Total Acreage Sprayed -->
          <div class="summary-item" *ngIf="totalAcreageSprayed > 0">
            <div class="summary-label text-muted">Total Sprayed</div>
            <div class="summary-value d-flex align-items-center gap-2">
              <span>{{ totalAcreageSprayed | number:'1.1-1' }} acres</span>
              <span *ngIf="acreageRemaining === 0" class="badge bg-success-subtle text-success">Complete</span>
              <span *ngIf="acreageRemaining > 0" class="badge bg-warning-subtle text-warning">{{ acreageRemaining | number:'1.0-0' }} remaining</span>
            </div>
          </div>

          <!-- Applied Chemicals Summary -->
          <div class="summary-item" *ngIf="appliedChemicals.length > 0">
            <div class="summary-label text-muted">Chemicals ({{ appliedChemicals.length }})</div>
            <div class="summary-chemicals mt-1">
              <div *ngFor="let chem of appliedChemicals; trackBy: trackByChemicalId" class="d-flex justify-content-between small py-1 border-bottom border-light">
                <span class="text-truncate mw-140px" [title]="chem.name">{{ chem.name }}</span>
                <span class="text-muted">{{ chem.rate }}</span>
              </div>
            </div>
          </div>
          <div class="summary-item" *ngIf="appliedChemicals.length === 0">
            <div class="summary-label text-muted">Chemicals</div>
            <div class="summary-value text-muted fst-italic">None added</div>
          </div>

          <!-- Total Chemical Usage Summary -->
          <div class="summary-item" *ngIf="totalChemicalUsage.length > 0">
            <div class="summary-label text-muted d-flex align-items-center gap-1">
              Total Usage
              <i class="bi bi-info-circle small" title="Calculated from acreage × rate for all drones"></i>
            </div>
            <div class="summary-chemicals mt-1">
              <div *ngFor="let usage of totalChemicalUsage; trackBy: trackByUsageIndex" class="d-flex justify-content-between small py-1 border-bottom border-light">
                <span class="text-truncate mw-120px" [title]="usage.chemicalName">{{ usage.chemicalName }}</span>
                <span class="fw-medium text-primary">{{ usage.totalAmount }}</span>
              </div>
            </div>
          </div>

          <div class="summary-item" *ngIf="selectedCertification">
            <div class="summary-label text-muted">FAA 44807</div>
            <div class="summary-value">{{ selectedCertification.certNumber }}</div>
          </div>

          <hr class="my-3" *ngIf="selectedField || appliedChemicals.length > 0">

          <!-- Safety Checklist -->
          <div class="mb-3">
            <div class="form-check mb-2">
              <input class="form-check-input" type="checkbox" id="preFlightChecklist"
                     formControlName="preFlightChecklist">
              <label class="form-check-label" for="preFlightChecklist">
                Pre-flight checklist completed
              </label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="bufferZonesVerified"
                     formControlName="bufferZonesVerified">
              <label class="form-check-label" for="bufferZonesVerified">
                Buffer zones verified
              </label>
            </div>
          </div>

          <hr class="my-3">

          <!-- Validation Errors -->
          <div *ngIf="validationErrors.length > 0" class="alert alert-danger py-2 mb-3">
            <div class="fw-medium mb-1"><i class="bi bi-exclamation-circle me-1"></i>Please fix the following:</div>
            <ul class="mb-0 ps-3 small">
              <li *ngFor="let error of validationErrors">{{ error }}</li>
            </ul>
          </div>

          <!-- Generic Errors -->
          <div *ngIf="genericError" class="alert alert-danger py-2 mb-3" role="alert" aria-live="assertive">
            <div class="fw-medium mb-1"><i class="bi bi-exclamation-circle me-1" aria-hidden="true"></i>{{ genericError }}</div>
          </div>

          <!-- Action Buttons -->
          <div class="d-grid gap-2">
            <button type="submit" class="btn btn-primary" [disabled]="isSaving || hasBlockingFlightConflicts">
              <ng-container *ngIf="!isSaving">
                <i class="bi bi-check-lg me-2"></i>{{ isEditMode ? 'Update Record' : 'Save Record' }}
              </ng-container>
              <ng-container *ngIf="isSaving">
                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                Saving...
              </ng-container>
            </button>
            <button type="button" class="btn btn-outline-secondary" (click)="onCancel()">
              Cancel
            </button>
          </div>
        </div>
      </div>
     </div>
    </div>
  </div>
</form>

<!-- Field Quick Add Modal -->
<app-field-quick-add
  #fieldQuickAdd
  (fieldCreated)="onFieldCreated($event)"
></app-field-quick-add>

<!-- Flight Map Modal -->
<div *ngIf="showFlightMapModal" class="flight-map-modal-overlay" (click)="closeFlightMapModal()">
  <div class="flight-map-modal" (click)="$event.stopPropagation()">
    <div class="flight-map-modal-header">
      <h5 class="mb-0">
        <i class="bi bi-map me-2"></i>
        <ng-container *ngIf="flightMapModalFlight && showUploadOverlay">Upload Flight Path</ng-container>
        <ng-container *ngIf="flightMapModalFlight && !showUploadOverlay">{{ flightMapModalFlight.flightPath?.name || 'Flight Path' }}</ng-container>
        <ng-container *ngIf="!flightMapModalFlight">All Flight Paths</ng-container>
      </h5>
      <button type="button" class="btn-close" (click)="closeFlightMapModal()"></button>
    </div>
    <div class="flight-map-modal-body">
      <app-flight-map
        [flightPath]="flightMapModalFlight?.flightPath ?? null"
        [flightPaths]="flightMapModalFlight ? [] : getAllFlightPaths()"
        [fieldCenter]="getFieldCenter()"
        [showUploadOverlay]="showUploadOverlay"
        [height]="'500px'"
        (fileUploaded)="onFlightPathUploaded($event)"
        (uploadCancelled)="closeFlightMapModal()">
      </app-flight-map>
    </div>
    <div class="flight-map-modal-footer" *ngIf="flightMapModalFlight?.flightPath as path">
      <div class="d-flex justify-content-between align-items-center" *ngIf="!showUploadOverlay">
        <div class="small text-muted">
          <i class="bi bi-file-earmark me-1"></i>{{ path.fileName }}
          <span class="mx-2">•</span>
          <i class="bi bi-rulers me-1"></i>{{ path.areaAcres | number:'1.1-1' }} acres
        </div>
        <button type="button" class="btn btn-primary btn-sm" (click)="closeFlightMapModal()">
          Done
        </button>
      </div>
    </div>
  </div>
</div>
  
<!-- Drag and drop overlay -->
 <div *ngIf="isDragging" class="drag-overlay">
   <div class="drag-overlay-content">
     <i class="bi bi-upload"></i>
     <div>Drop your flight file here</div>
     <div class="small">KML or ZIP</div>
   </div>
 </div>
